import { Link } from "react-router-dom";
import type { Drama } from "@/types";

interface DramaCardProps {
  drama: Drama;
  variant?: "grid" | "list";
}

export default function DramaCard({ drama, variant = "grid" }: DramaCardProps) {
  const statusColor =
    drama.status === "更新中"
      ? "bg-accent-500 text-accent-950"
      : drama.status === "待播"
        ? "bg-secondary-200 text-secondary-900"
        : "bg-primary-100 text-primary-700";

  if (variant === "list") {
    return (
      <Link
        to={`/detail/drama/${drama.id}`}
        className="group flex gap-4 rounded-xl border border-background-200/70 bg-background-50 p-3 transition-colors hover:border-primary-200 cursor-pointer"
      >
        <div className="relative h-32 w-24 shrink-0 overflow-hidden rounded-lg bg-background-200">
          <img
            src={drama.cover}
            alt={`${drama.title} 海报`}
            title={`${drama.title} 韩剧详情`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-center gap-2">
            <h3 className="truncate font-heading text-base font-600 text-foreground-950 group-hover:text-primary-600">
              {drama.title}
            </h3>
            <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs ${statusColor}`}>
              {drama.status}
            </span>
          </div>
          <p className="mt-1 text-xs text-foreground-500">
            {drama.year} · {drama.genres.slice(0, 2).join(" / ")} · {drama.episodes}集
          </p>
          <p className="mt-2 line-clamp-2 text-sm text-foreground-600">{drama.synopsis[0]}</p>
          <div className="mt-auto flex items-center justify-between pt-2">
            <span className="text-xs text-foreground-500">主演 {drama.cast.slice(0, 2).join("、")}</span>
            <span className="flex items-center gap-1 text-sm font-600 text-accent-600">
              <i className="ri-star-fill text-xs"></i>
              {drama.rating.toFixed(1)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/drama/${drama.id}`}
      className="group flex flex-col overflow-hidden rounded-xl border border-background-200/70 bg-background-50 transition-colors hover:border-primary-200 cursor-pointer"
    >
      <div className="relative aspect-[3/4] w-full overflow-hidden bg-background-200">
        <img
          src={drama.cover}
          alt={`${drama.title} 海报`}
          title={`${drama.title} 韩剧剧情介绍`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-xs font-500 ${statusColor}`}>
          {drama.status}
        </span>
        <span className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-foreground-950/70 px-2.5 py-1 text-xs font-600 text-background-50">
          <i className="ri-star-fill text-[10px] text-accent-400"></i>
          {drama.rating.toFixed(1)}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-4">
        <h3 className="truncate font-heading text-base font-600 text-foreground-950 group-hover:text-primary-600">
          {drama.title}
        </h3>
        <p className="mt-1.5 truncate text-xs text-foreground-500">
          {drama.year} · {drama.genres.join(" / ")}
        </p>
        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
          {drama.synopsis[0]}
        </p>
        <div className="mt-3 flex items-center gap-1 pt-3 text-xs text-foreground-500 border-t border-background-200/70">
          <i className="ri-user-3-line text-sm"></i>
          <span className="truncate">{drama.cast.slice(0, 3).join("、")}</span>
        </div>
      </div>
    </Link>
  );
}